import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Instagram, Facebook, Twitter, Mail, MapPin } from "lucide-react";

const Footer = () => {
  const { t } = useLang();
  return (
    <footer className="bg-[#2C1E16] text-[#FAF9F6] mt-24" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src="/favicon.png" alt="Amazypet" className="h-12 w-12" />
            <span className="font-serif text-2xl text-[#D4AF37]">Amazypet</span>
          </div>
          <p className="text-sm text-[#FAF9F6]/70 leading-relaxed">{t("footer.tagline")}</p>
          <div
            className="mt-5 flex items-start gap-2 text-xs text-[#FAF9F6]/60 leading-relaxed"
            data-testid="footer-address"
          >
            <MapPin size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
            <span>
              {t("footer.headquarters")}
              <br />
              Νικολάου Πλαστήρα 17, Άγιοι Ανάργυροι
              <br />
              <span className="text-[#FAF9F6]/40">{t("footer.notShowroom")}</span>
            </span>
          </div>
        </div>
        <div>
          <p className="overline mb-4 text-[#D4AF37]">{t("footer.shop")}</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/shop?category=skylos" className="hover:text-[#D4AF37] transition">Σκύλος / Dog</Link></li>
            <li><Link to="/shop?category=gata" className="hover:text-[#D4AF37] transition">Γάτα / Cat</Link></li>
            <li><Link to="/shop?category=ptina" className="hover:text-[#D4AF37] transition">Πτηνά / Birds</Link></li>
            <li><Link to="/shop?category=psaria" className="hover:text-[#D4AF37] transition">Ψάρια / Fish</Link></li>
          </ul>
        </div>
        <div>
          <p className="overline mb-4 text-[#D4AF37]">Info</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/careers" className="hover:text-[#D4AF37] transition" data-testid="footer-careers">{t("nav.careers")}</Link></li>
            <li>{t("footer.shipping")}</li>
            <li>{t("footer.terms")}</li>
            <li>{t("footer.privacy")}</li>
            <li>{t("footer.contact")}</li>
          </ul>
        </div>
        <div>
          <p className="overline mb-4 text-[#D4AF37]">{t("footer.followUs")}</p>
          <div className="flex gap-4">
            <a href="#" className="hover:text-[#D4AF37] transition" aria-label="Instagram"><Instagram size={20} /></a>
            <a href="#" className="hover:text-[#D4AF37] transition" aria-label="Facebook"><Facebook size={20} /></a>
            <a href="#" className="hover:text-[#D4AF37] transition" aria-label="Twitter"><Twitter size={20} /></a>
            <a href="mailto:hello@amazypet.com" className="hover:text-[#D4AF37] transition" aria-label="Email"><Mail size={20} /></a>
          </div>
          <p className="mt-6 text-xs text-[#FAF9F6]/50">
            Δεχόμαστε: Visa, Mastercard, Apple Pay, Google Pay, PayPal, Τραπεζική Κατάθεση
          </p>
        </div>
      </div>
      <div className="border-t border-[#FAF9F6]/10 py-5 px-6 lg:px-10">
        <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-xs text-[#FAF9F6]/40 flex-wrap">
          <span>{t("footer.copy")}</span>
          <span className="hidden sm:inline text-[#FAF9F6]/20">·</span>
          <a
            href="https://app.emergent.sh/?utm_source=emergent-badge"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[#FAF9F6]/40 hover:text-[#D4AF37] transition-colors"
            data-testid="emergent-credit"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 16 16" fill="none">
              <path d="M15.5702 8.13142C15.7729 8.0412 16.0007 8.18878 15.9892 8.4103C15.8374 11.3192 14.0965 14.0405 11.2531 15.3065C8.40964 16.5725 5.2224 16.0453 2.95912 14.2117C2.78676 14.072 2.82955 13.804 3.03219 13.7137L4.95677 12.8568C5.04866 12.8159 5.15446 12.823 5.24204 12.8725C6.73377 13.7153 8.59176 13.8649 10.2772 13.1145C11.9626 12.3641 13.0947 10.8833 13.4665 9.21075C13.4883 9.11256 13.5539 9.02918 13.6457 8.98827L15.5702 8.13142Z" fill="currentColor" />
              <path fillRule="evenodd" clipRule="evenodd" d="M15.3066 4.74698L15.5067 5.19653C15.5759 5.35178 15.5061 5.53366 15.3508 5.60278L1.29992 11.8586C1.14467 11.9278 0.962794 11.8579 0.893675 11.7027L0.701732 11.2716L0.693457 11.2531C-1.10317 7.21778 0.711626 2.49007 4.74692 0.693443C8.78221 -1.10318 13.51 0.711693 15.3066 4.74698ZM2.82356 8.55367C2.63552 8.63739 2.41991 8.51617 2.40853 8.31065C2.28373 6.05724 3.53858 3.85787 5.72286 2.88536C7.90715 1.91286 10.3813 2.45199 11.9724 4.05256C12.1175 4.19854 12.0633 4.43988 11.8753 4.5236L2.82356 8.55367Z" fill="currentColor" />
            </svg>
            <span>Made with Emergent</span>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
